import { isOk, Ok, Result } from "@pacote/result"
import { useQuery } from "@tanstack/react-query"
import { z } from "zod"

import { ListboxOption } from "@/components/ui/select-field"

import {
  ApiFailure,
  BaseApiErrorResponse,
  baseApiResponseSchema,
  HandledApiError,
  UnhandledApiError,
} from "./api-config"
import { baseClientFetcher } from "./base-fetchers"

/* -------------------------------------------------------------------------------------------------
 * types
 * -----------------------------------------------------------------------------------------------*/
type CountriesPayload = {
  current?: boolean
}

const countrySchema = z.object({
  description: z.string(),
  fiscalCode: z.string(),
  sign: z.string(),
  callingCodes: z.array(z.string()).optional(),
  actual: z.boolean(),
})

const countriesResponseSchema = baseApiResponseSchema.extend({
  countries: z.array(countrySchema),
})

type CountriesResponse = z.infer<typeof countriesResponseSchema>

type CountriesServiceResponse = Result<
  {
    normalizedCountries: { [key: string]: ListboxOption }
    phonePrefixCountries: ListboxOption[]
  },
  void
>

/* -------------------------------------------------------------------------------------------------
 * config
 * -----------------------------------------------------------------------------------------------*/
const countriesServiceEndpoint = "/residence-core-ms/v1/geo/countries"

/* -------------------------------------------------------------------------------------------------
 * fetcher
 * -----------------------------------------------------------------------------------------------*/
const countriesFetcher = async (
  payload: CountriesPayload,
): Promise<CountriesServiceResponse> => {
  const responseResult = await baseClientFetcher<
    CountriesResponse,
    BaseApiErrorResponse
  >({
    url: countriesServiceEndpoint,
    params: {
      current: !!payload?.current,
      enableItaly: true,
    },
    successSchemaValidator: countriesResponseSchema,
    enrichHeaders: true,
  })

  if (isOk(responseResult)) {
    const { countries } = responseResult.value

    const normalizedCountries: { [key: string]: ListboxOption } = {}
    const phonePrefixCountries: ListboxOption[] = []

    countries.forEach((country) => {
      const { sign, callingCodes } = country
      const iconSrc = sign
        ? `/countries-flags/${sign}.svg`
        : `/countries-flags/- .svg`

      if (callingCodes) {
        for (let j = 0, len = callingCodes.length; j < len; j++) {
          const callingCode = callingCodes[j]
          const optionId = "+" + callingCode
          if (callingCode) {
            phonePrefixCountries.push({
              optionId,
              label: optionId,
              iconSrc,
            })
          }
        }
      }

      normalizedCountries[country.description] = {
        label: country.description,
        optionId: country.fiscalCode,
        iconSrc,
      }
    })
    return Ok({ normalizedCountries, phonePrefixCountries })
  }

  const { status } = responseResult.value

  if (status != 422)
    throw new UnhandledApiError({
      originalStatus: status as number,
      status: ApiFailure.UNPROCESSABLE_ERROR,
      message: `A non 422 status was returned at ${countriesServiceEndpoint}`,
    })

  throw new HandledApiError({
    data: responseResult.value.data,
    type: "Failure",
  })
}

/* -------------------------------------------------------------------------------------------------
 * hooks
 * -----------------------------------------------------------------------------------------------*/

const useCountriesQuery = (payload: CountriesPayload, enabled = true) => {
  const { data, isLoading, isFetching, ...rest } = useQuery({
    queryKey: [countriesServiceEndpoint],
    queryFn: async () => countriesFetcher(payload),
    enabled,
    staleTime: Infinity,
  })

  const normalizedCountries =
    data && isOk(data) ? data.value.normalizedCountries : {}

  return {
    countries: data,
    normalizedCountries,
    isCountriesQueryLoading: isLoading || isFetching,
    ...rest,
  }
}

export { countriesFetcher, useCountriesQuery }
export type { CountriesServiceResponse }
